import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { navSections, isNavItemActive, isNavItemUnlocked, type NavItem } from "./navConfig";
import { PHONE_ACTIONS_ID } from "./PageHeaderActions";
import { useInstalledModules } from "../../lib/useInstalledModules";
import { usePermissions } from "../../lib/permissions";

// Owner on a phone (canvas v2-om-nav-*, ruled 2026-10-18): the guest app's bottom bar. Today, Bills, a raised coral key,
// Catalog, More. The raised key opens a sheet: this page's own keys first (PageHeaderActions portals them into it), then
// quick adds that open another page's main key.

const TODAY = ["/", "/sales", "/analytics"];
const QUICK: Array<{ to: string; icon: string; label: string }> = [
  { to: "/menu", icon: "restaurant_menu", label: "Menu item" },
  { to: "/inventory", icon: "inventory_2", label: "Product" },
  { to: "/marketing", icon: "sell", label: "Promotion" },
  { to: "/staff", icon: "person_add", label: "Team member" },
  { to: "/customers", icon: "group_add", label: "Customer" },
  { to: "/devices", icon: "add_link", label: "Pair a device" },
  { to: "/purchasing", icon: "local_shipping", label: "Supplier" },
];

/** The nav items this login can open: module on and permission held. */
export function useOpenItems() {
  const { modules } = useInstalledModules();
  const { can } = usePermissions();
  const items = navSections.flatMap((s) => s.items).filter((i) => isNavItemUnlocked(i, modules) && can(i.permission));
  const sections = navSections.map((s) => ({ ...s, items: s.items.filter((i) => items.includes(i)) })).filter((s) => s.items.length > 0);
  return { items, sections };
}

const itemFor = (items: NavItem[], to: string) => items.find((i) => i.to === to);

export function PhoneNav() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { items, sections } = useOpenItems();
  const [open, setOpen] = useState(false);
  const pendingAdd = useRef<string | null>(null);
  // The keys the last page left in the sheet: a lazy page can still be showing them right after the route changes.
  const staleKeys = useRef(new WeakSet<Element>());

  const catalog = sections.find((s) => s.section === "Catalog");
  const catalogTo = (itemFor(items, "/inventory") ?? itemFor(items, "/menu") ?? catalog?.items[0])?.to;
  const inCatalog = !!catalog?.items.some((i) => isNavItemActive(i, pathname));
  const bills = itemFor(items, "/bills");
  const quick = QUICK.filter((q) => itemFor(items, q.to) && q.to !== pathname);
  const lit = TODAY.includes(pathname) ? "today" : bills && isNavItemActive(bills, pathname) ? "bills" : inCatalog ? "catalog" : "more";

  useEffect(() => setOpen(false), [pathname]);

  // A quick add goes to its page, then presses that page's main key as soon as the page has put it in the sheet.
  useEffect(() => {
    if (pendingAdd.current !== pathname) return;
    const box = document.getElementById(PHONE_ACTIONS_ID);
    if (!box) return;
    const press = () => {
      const key = box.querySelector<HTMLButtonElement>(":scope > :not(.o-btn--line)");
      if (!key || key.disabled || staleKeys.current.has(key)) return false;
      pendingAdd.current = null;
      key.click();
      return true;
    };
    if (press()) return;
    const watch = new MutationObserver(() => press() && watch.disconnect());
    watch.observe(box, { childList: true, subtree: true, attributes: true });
    const giveUp = setTimeout(() => {
      watch.disconnect();
      pendingAdd.current = null;
    }, 4000);
    return () => {
      watch.disconnect();
      clearTimeout(giveUp);
    };
  }, [pathname]);

  const tab = (key: string, to: string | undefined, icon: string, label: string) =>
    to && (
      <Link to={to} className={lit === key ? "is-on" : undefined} aria-current={lit === key ? "page" : undefined}>
        <span className="ms" aria-hidden="true">{icon}</span>
        {label}
      </Link>
    );

  return (
    <>
      {open && <div className="o-qshade" onClick={() => setOpen(false)} />}
      <section className={`o-qsheet${open ? " is-open" : ""}`} role="dialog" aria-label="New">
        <span className="o-qsheet__grab" aria-hidden="true" />
        <div className="o-gl o-qsheet__here">On this page</div>
        {/* PageHeaderActions puts the page's keys here; a pick closes the sheet */}
        <div id={PHONE_ACTIONS_ID} className="o-qacts" onClickCapture={() => setOpen(false)} />
        {quick.length > 0 && (
          <>
            <div className="o-gl">Add anywhere</div>
            <div className="o-qgrid">
              {quick.map((q) => (
                <button
                  type="button"
                  key={q.to}
                  onClick={() => {
                    pendingAdd.current = q.to;
                    staleKeys.current = new WeakSet(document.getElementById(PHONE_ACTIONS_ID)?.children ?? []);
                    setOpen(false);
                    void navigate({ to: q.to });
                  }}
                >
                  <span className="ms" aria-hidden="true">{q.icon}</span>
                  {q.label}
                </button>
              ))}
            </div>
          </>
        )}
      </section>
      <nav className="o-bnav" aria-label="Owner Portal">
        {tab("today", "/", "dashboard", "Today")}
        {tab("bills", bills?.to, "receipt_long", "Bills")}
        <button type="button" className={`o-bnav__raise${open ? " is-open" : ""}`} aria-label={open ? "Close" : "New"} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span className="ms" aria-hidden="true">{open ? "close" : "add"}</span>
        </button>
        {tab("catalog", catalogTo, "inventory_2", "Catalog")}
        {tab("more", "/more", "menu", "More")}
      </nav>
    </>
  );
}
